

import { Getter, namespace } from "vuex-class"
import { Component, Vue } from "vue-property-decorator"

import DataForm from "@/components/Config/DataForm.vue"
import UserFilterFieldSetup from "./UserFilterFieldSetup.vue"
import UserExcludeFromAvailableSetup from "./UserExcludeFromAvailableSetup.vue"

import { TimeManagementSettingsWithShiftPatterns } from "@/types/timeManagement"
import ShiftPatternSetup from "./ShiftPatternSetup.vue"

import api from "@/services/api"
import { TenantFeatures } from "@/types/tenantFeatures"
import { Field, DataSourceItem } from "@/types"
import { CalculationType } from "@/types/field"
import { FieldTypeId } from "@/types/fieldTypes"

const setup = namespace("setup")

@Component({
	components: {
		"data-form": DataForm
	}
})
export default class TimeManagement extends Vue {
	@setup.State fields!: Field[]

	@setup.State userFields!: Field[]

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@setup.Action loadFieldsIfUnloaded!: () => Promise<void>

	timeManagementSettings: TimeManagementSettingsWithShiftPatterns | null = null

	private listTypeUserFieldsOptions: { [userFieldId: string]: DataSourceItem[] } = {}

	get dateFields (): Field[] {
		const dateFields = this.fields.filter(f => f.dataType === "date" || f.dataType === "date-time")
		const calculatedDateFields = this.fields.filter(f =>
			f.dataType === FieldTypeId.Calculated && f.extensions?.["calculationType"] === CalculationType.AddToDate
		)

		return dateFields.concat(calculatedDateFields)
	}

	get columns () {
		return [
			{
				key: "enabled",
				label: "Enabled",
				dataType: "checkbox"
			},
			{
				key: "assignmentDateFieldId",
				label: "Scheduled Date Field",
				dataType: "list",
				options: this.dateFields
			},
			{
				key: "shareHours",
				label: "Share Hours between Assigned Users",
				dataType: "checkbox",
				readonly: !this.timeManagementSettings || this.timeManagementSettings.overflowHours
			},
			{
				key: "overflowHours",
				label: "Split hours across days",
				dataType: "checkbox",
				readonly: !this.timeManagementSettings || this.timeManagementSettings.shareHours,
				inForm: this.getIsFeatureEnabled(TenantFeatures.TimeManagementOverflow)
			},
			{
				key: "trimExcessHours",
				label: "Automatically cut excess assigned hours",
				description: "This will remove hours to prevent overallocation. Hours will be removed from latest dates first, and for assignments on the same day, those set later will be removed first.",
				dataType: "checkbox",
				inForm: this.timeManagementSettings && this.timeManagementSettings.overflowHours
			},
			{
				key: "noAdditionalAutoSetAssignment",
				label: "Do not automatically create an assignment for additionally assigned users",
				description: "This will only create an assignment automatically for the first user assigned to a field. Additional users will need to be assigned manually.",
				dataType: "checkbox",
				inForm: this.timeManagementSettings && this.timeManagementSettings.overflowHours
			},
			{
				key: "defaultHoursWhenUnset",
				label: "Default hours",
				description: "If a user is assigned time on a date where no shift is set, then this is how many hours capacity to default to.",
				dataType: "number",
				inForm: this.timeManagementSettings && this.timeManagementSettings.overflowHours
			},
			{
				key: "userFilterFields",
				label: "User Filters",
				dataType: "component",
				dynamicComponent: UserFilterFieldSetup
			},
			{
				key: "excludeFromAvailable",
				label: "Exclude Users From Available",
				dataType: "component",
				dynamicComponent: UserExcludeFromAvailableSetup,
				componentProperties: {
					listTypeUserFields: this.listTypeUserFields,
					listTypeUserFieldsOptions: this.listTypeUserFieldsOptions
				}
			},
			{
				key: "shiftPatterns",
				label: "Shift Patterns",
				dataType: "component",
				dynamicComponent: ShiftPatternSetup,
				inForm: this.timeManagementShiftPatternConfigEnabled
			}
		]
	}

	get timeManagementShiftPatternConfigEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TimeManagementShiftPatternConfig)
	}

	get displayedColumns () {
		return this.columns.filter(c => c.inForm === undefined || c.inForm === true)
	}

	/**
	 * Gets if the current settings are valid. All User Filter Fields must have a user field and a record field set.
	 * Also ensures no duplicate or empty shift pattern titles.
	 * @returns {boolean} If current settings are valid.
	 */
	get valid (): boolean {
		if (!this.timeManagementSettings) return false

		const userFiltersValid = !this.timeManagementSettings.userFilterFields.some(f => !f.userField || !f.recordField)

		const patterns = this.timeManagementSettings.shiftPatterns ?? []
		const hasEmptyTitle = patterns.some(sp => !sp.title || !sp.title.trim())
		const titles = patterns.map(sp => (sp.title || "").trim()).filter(t => t)
		const noDuplicates = titles.length === new Set(titles).size

		return userFiltersValid && !hasEmptyTitle && noDuplicates
	}

	/**
	 * On mount, load time management settings, defaulting if none are configured.
	 * Also load options for list type user fields.
	 */
	async mounted () {
		await this.loadFieldsIfUnloaded()

		const timeManagementSettings = (await api.timeManagementSettings()) || {
			enabled: true,
			assignmentDateFieldId: null
		}

		if (!timeManagementSettings.userFilterFields) {
			timeManagementSettings.userFilterFields = []
		}

		if (this.timeManagementShiftPatternConfigEnabled) {
			try {
				const shiftPatterns = await api.shiftPatterns()
				this.timeManagementSettings = {
					...timeManagementSettings,
					shiftPatterns
				}
			} catch (error) {
				console.warn("Failed to load shift patterns", error)
				this.timeManagementSettings = timeManagementSettings
			}
		} else {
			this.timeManagementSettings = timeManagementSettings
		}

		this.listTypeUserFields.forEach(async f => {
			if (f.dataSourceId) {
				this.listTypeUserFieldsOptions[f.id!] = await api.dataSourceGetAll(f.dataSourceId, true)
			}
		})
	}

	/**
	 * Saves current settings
	 */
	async save () {
		if (this.timeManagementSettings && this.timeManagementSettings.defaultHoursWhenUnset === null) {
			this.timeManagementSettings.defaultHoursWhenUnset = 9
		}

		await api.setTimeManagementSettings(this.timeManagementSettings!)

		if (this.timeManagementShiftPatternConfigEnabled && this.timeManagementSettings?.shiftPatterns) {
			this.timeManagementSettings.shiftPatterns = await api.updateShiftPatterns(this.timeManagementSettings.shiftPatterns)
		}
	}

	private get listTypeUserFields (): Field[] {
		return this.userFields.filter(f => !!f.dataType && ["list", "list-multiple"].includes(f.dataType))
	}
}
